// @ts-nocheck
/*
 * Full-page Edit Contact — replaces the modal that used to live inside
 * AllContactsPage.tsx. Same validation rules and payload shape as before
 * (first name + 10-digit primary required; alt numbers must be 10 digits
 * or empty; masked read-only when agent masking is on). Uses the row
 * data passed via router `location.state` when the caller has it; falls
 * back to fetching the flat list and finding the id when the user lands
 * on the URL directly.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CustomFieldsSection } from './custom-fields/CustomFieldsSection';
import { useCustomFieldTemplates } from './custom-fields/useCustomFieldTemplates';
import { useContactCustomFieldsForm } from './custom-fields/useContactCustomFieldsForm';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';

import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_ALL_CONTACTS_LIST,
  API_CONTACT_GROUP_MEMBERS_UPDATE,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { getAgentMasking, maskNumber } from '@/utilities/cliDisplay';
import { ROUTES } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { contactFieldError } from '@/utilities/contactFieldRules';

const CONTACT_TEXT_LABELS = {
  first_name: 'First name',
  last_name: 'Last name',
  street: 'Street',
  city: 'City',
  state: 'State',
  country: 'Country',
  company: 'Company',
};

const emptyContact = {
  id: null,
  first_name: '',
  last_name: '',
  street: '',
  city: '',
  state: '',
  country: '',
  company: '',
  primary_number: '',
  alt1_number: '',
  alt2_number: '',
};

const ContactEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();

  const [contact, setContact] = useState<any>(
    location?.state?.contact ? { ...emptyContact, ...location.state.contact } : null,
  );
  const [loading, setLoading] = useState(!location?.state?.contact);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);

  const maskNumbers = getAgentMasking(getUser());

  const {
    enabled: customFieldsEnabled,
    templates: customTemplates,
    loading: customTemplatesLoading,
  } = useCustomFieldTemplates();
  const customForm = useContactCustomFieldsForm(customTemplates, customTemplatesLoading);
  const customInitFor = useRef(null);

  // Preselect the contact's last template, its saved values and added fields once.
  useEffect(() => {
    if (!contact?.id || customTemplatesLoading || customInitFor.current === contact.id) return;
    customInitFor.current = contact.id;
    customForm.load(contact);
  }, [contact?.id, customTemplates, customTemplatesLoading]);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  // If we didn't come in via row-click (state prefilled), fetch the flat
  // list and pick the row by id. Same source-of-truth as the list page.
  useEffect(() => {
    if (contact) return;
    setLoading(true);
    getApiCall(API_ALL_CONTACTS_LIST, {})
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : resp?.data ?? [];
        const found = list.find((c: any) => String(c.id) === String(id));
        if (found) {
          setContact({ ...emptyContact, ...found });
        } else {
          toast.error('Contact not found');
          navigate(`/${ROUTES.CONTACTGROUP.ALL_CONTACTS}`);
        }
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && String(err).includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || 'Failed to load contact');
          navigate(`/${ROUTES.CONTACTGROUP.ALL_CONTACTS}`);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (name: string, value: any) =>
    setContact((prev: any) => ({ ...prev, [name]: value }));

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    const fieldError = name in CONTACT_TEXT_LABELS ? contactFieldError(name, CONTACT_TEXT_LABELS[name], value) : null;
    if (fieldError) {
      toast.error(fieldError);
      return;
    }
    setField(name, value.replace(/^\s+/, ''));
  };

  const handleBlurTrim = (e: any) => setField(e.target.name, (e.target.value ?? '').trim());

  const handleSave = (e: any) => {
    e.preventDefault();
    if (!contact) return;

    const trimmed = {
      ...contact,
      first_name: contact.first_name?.trim(),
      last_name: contact.last_name?.trim(),
      street: contact.street?.trim(),
      city: contact.city?.trim(),
      state: contact.state?.trim(),
      country: contact.country?.trim(),
      company: contact.company?.trim(),
      primary_number: contact.primary_number?.trim(),
      alt1_number: contact.alt1_number?.trim(),
      alt2_number: contact.alt2_number?.trim(),
    };

    if (!trimmed.first_name || !trimmed.primary_number) {
      toast.error('First name and primary number are required');
      return;
    }
    if (!/^\d{10}$/.test(trimmed.primary_number)) {
      toast.error('Primary number must be exactly 10 digits');
      return;
    }
    if (trimmed.alt1_number && !/^\d{10}$/.test(trimmed.alt1_number)) {
      toast.error('Alternate number 1 must be exactly 10 digits or empty');
      return;
    }
    if (trimmed.alt2_number && !/^\d{10}$/.test(trimmed.alt2_number)) {
      toast.error('Alternate number 2 must be exactly 10 digits or empty');
      return;
    }

    for (const [key, label] of Object.entries(CONTACT_TEXT_LABELS)) {
      const fieldError = trimmed[key] ? contactFieldError(key, label, trimmed[key]) : null;
      if (fieldError) {
        toast.error(fieldError);
        return;
      }
    }

    const payload = {
      first_name: trimmed.first_name,
      last_name: trimmed.last_name,
      street: trimmed.street,
      city: trimmed.city,
      state: trimmed.state,
      country: trimmed.country,
      company: trimmed.company,
      primary_number: trimmed.primary_number,
      alt1_number: trimmed.alt1_number || null,
      alt2_number: trimmed.alt2_number || null,
    };

    const customError = customForm.validate();
    if (customError) {
      toast.error(customError);
      return;
    }
    if (customFieldsEnabled) Object.assign(payload, customForm.payload(true));

    setSaving(true);
    const url = API_CONTACT_GROUP_MEMBERS_UPDATE.replace(':id', contact.id);
    postApiCall(url, payload)
      .then(() => {
        toast.success('Contact updated successfully');
        navigate(`/${ROUTES.CONTACTGROUP.ALL_CONTACTS}`);
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && String(err).includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err) || 'Failed to update contact');
        }
      })
      .finally(() => setSaving(false));
  };

  if (loading || !contact) {
    return (
      <ScrollPage>
        <PageHeader
          title="Edit Contact"
          description="Update contact details."
          actions={
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          }
        />
        <Loader />
      </ScrollPage>
    );
  }

  const nm =
    `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || `#${contact.id}`;

  return (
    <ScrollPage>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span>Edit Contact</span>
            <span className="text-base font-normal text-muted-foreground">
              / <span className="font-semibold text-foreground">{nm}</span>
            </span>
          </span>
        }
        description="Update contact details."
        actions={
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        }
      />

      <Card className="p-6">
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                First Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="first_name"
                value={contact.first_name || ''}
                onChange={handleInputChange}
                onBlur={handleBlurTrim}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                Last Name
              </label>
              <input
                type="text"
                className="form-control"
                name="last_name"
                value={contact.last_name || ''}
                onChange={handleInputChange}
                onBlur={handleBlurTrim}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                Primary Number <span className="text-destructive">*</span>
              </label>
              <input
                type="tel"
                className="form-control"
                name="primary_number"
                value={
                  maskNumbers ? maskNumber(contact.primary_number || '') : contact.primary_number || ''
                }
                readOnly={maskNumbers}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setField('primary_number', cleaned);
                }}
                onBlur={handleBlurTrim}
                maxLength={10}
                pattern="[0-9]{10}"
                required
              />
              <small className="mt-1 block text-[11px] text-muted-foreground">
                {maskNumbers
                  ? 'Number hidden (masking enabled)'
                  : (contact.primary_number || '').length === 10
                    ? '✓ Valid 10-digit number'
                    : 'Enter 10 digits (numbers only)'}
              </small>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                Alternate Number 1
              </label>
              <input
                type="tel"
                className="form-control"
                name="alt1_number"
                value={
                  maskNumbers ? maskNumber(contact.alt1_number || '') : contact.alt1_number || ''
                }
                readOnly={maskNumbers}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setField('alt1_number', cleaned);
                }}
                onBlur={handleBlurTrim}
                maxLength={10}
                pattern="[0-9]{10}"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                Alternate Number 2
              </label>
              <input
                type="tel"
                className="form-control"
                name="alt2_number"
                value={
                  maskNumbers ? maskNumber(contact.alt2_number || '') : contact.alt2_number || ''
                }
                readOnly={maskNumbers}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setField('alt2_number', cleaned);
                }}
                onBlur={handleBlurTrim}
                maxLength={10}
                pattern="[0-9]{10}"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground/80">
              Street Address
            </label>
            <input
              type="text"
              className="form-control"
              name="street"
              value={contact.street || ''}
              onChange={handleInputChange}
              onBlur={handleBlurTrim}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">City</label>
              <input
                type="text"
                className="form-control"
                name="city"
                value={contact.city || ''}
                onChange={handleInputChange}
                onBlur={handleBlurTrim}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                State / Province
              </label>
              <input
                type="text"
                className="form-control"
                name="state"
                value={contact.state || ''}
                onChange={handleInputChange}
                onBlur={handleBlurTrim}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-foreground/80">
                Country
              </label>
              <input
                type="text"
                className="form-control"
                name="country"
                value={contact.country || ''}
                onChange={handleInputChange}
                onBlur={handleBlurTrim}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-foreground/80">
              Company
            </label>
            <input
              type="text"
              className="form-control"
              name="company"
              value={contact.company || ''}
              onChange={handleInputChange}
              onBlur={handleBlurTrim}
            />
          </div>

          {customFieldsEnabled && (
            <CustomFieldsSection {...customForm.sectionProps} disabled={saving} />
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => navigate(`/${ROUTES.CONTACTGROUP.ALL_CONTACTS}`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Card>
    </ScrollPage>
  );
};

export default ContactEditPage;
